import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { tenantApi } from '../../api/tenantApi';
import { Panel, Loader, EmptyState, useToast, errText } from '../ui';

// ============================================================================
// DbSizesMonitoring
//
// Source: GET /api/AdminStorage/GetStorageInfo?ClientCode=<code>
//
// Flow: pick clients in the dropdown (none selected initially) -> run_scan
// calls the API ONE CLIENT AT A TIME -> results appear in tabs.
//
// Every field of the response is used somewhere:
//
//   response field                      where it shows
//   ----------------------------------  ---------------------------------------
//   summary.clientCode / clientName     overview (client cell), client_detail
//   summary.databaseAllocated/Used/Free overview (+ free %), client_detail
//   summary.fileStorageMB               overview, file_folders, client_detail
//                                        (cross-checked against files.totalSizeMB)
//   summary.totalAllocated/UsedMB       overview (+ usage %), client_detail
//   summary.calculatedOn                overview, client_detail
//   database.databaseName               overview, client_detail
//   database.dataFile / logFile (all)   db_files, overview (%), client_detail
//   tables[]                            tables (all clients), client_detail
//   files.totalSizeMB                   overview (mismatch check), client_detail
//   files.folders[] (all six fields)    file_folders (matrix + rollup), overview
//                                        (largest folder), client_detail
//   databaseProperties (all)            overview (recovery model / compat level /
//                                        collation / auto growth columns),
//                                        db_properties, overview (state / health),
//                                        client_detail
//   the raw JSON itself                 client_detail -> raw_response (copyable)
// ============================================================================

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const toList = (res) =>
  Array.isArray(res) ? res
    : Array.isArray(res?.data) ? res.data
    : Array.isArray(res?.result) ? res.result
    : Array.isArray(res?.tenants) ? res.tenants
    : [];

const codeOf = (t) => (typeof t === 'string' ? t : t?.clientCode ?? t?.ClientCode ?? t?.code ?? null);

const num = (v) => (typeof v === 'number' ? v : Number(v) || 0);
const mb = (v) => num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const size = (v) => {
  const n = num(v);
  return n >= 1024 ? `${(n / 1024).toFixed(2)} GB` : `${n.toFixed(2)} MB`;
};
const pctOf = (a, b) => (b > 0 ? (a / b) * 100 : 0);
const fmtPct = (p) => `${num(p).toFixed(1)}%`;
const lv = (p) => (p >= 85 ? 'hi' : p >= 60 ? 'mid' : 'lo');
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};
const isIsoDate = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v);
const humanKey = (k) => k.replace(/([A-Z])/g, ' $1').toLowerCase().trim();

const ago = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 365) return `${d}d ago`;
  return `${Math.floor(d / 365)}y ${Math.floor((d % 365) / 30)}mo ago`;
};

const bar = (done, total, width = 20) => {
  const p = total > 0 ? Math.min(1, done / total) : 0;
  const n = Math.round(p * width);
  return `[${'#'.repeat(n)}${'.'.repeat(width - n)}]`;
};

// { status: "Success", data: { summary, database, tables, files, databaseProperties } }
function unwrap(res) {
  if (res && typeof res.status === 'string' && res.status.toLowerCase() !== 'success') {
    throw new Error(res.message || res.msg || `API status: ${res.status}`);
  }
  const d = res?.data ?? res;
  if (!d || !d.summary) throw new Error('response has no data.summary');
  return d;
}

// things worth a human's attention, derived from the response
function healthFlags(r) {
  const f = [];
  if (r.state && String(r.state).toUpperCase() !== 'ONLINE') f.push({ t: `state ${r.state}`, lvl: 'hi' });
  if (r.isReadOnly) f.push({ t: 'read-only', lvl: 'hi' });
  if (r.totalPct >= 85) f.push({ t: 'total >85% full', lvl: 'hi' });
  if (r.dataPct >= 85) f.push({ t: 'data file >85%', lvl: 'hi' });
  if (r.logPct >= 85) f.push({ t: 'log file >85%', lvl: 'hi' });
  if (r.autoShrink) f.push({ t: 'auto-shrink on', lvl: 'mid' });
  if (r.autoClose) f.push({ t: 'auto-close on', lvl: 'mid' });
  if (r.filesMismatch) f.push({ t: 'file size mismatch', lvl: 'mid' });
  return f;
}

// flat row used by overview / db_files
function toRow(code, d) {
  const s = d.summary || {};
  const db = d.database || {};
  const dataFile = db.dataFile || {};
  const logFile = db.logFile || {};
  const props = d.databaseProperties || {};
  const folders = d.files?.folders || [];
  const filePct = (f) => num(f.usagePercent ?? pctOf(num(f.usedMB), num(f.allocatedMB)));
  const totalAllocated = num(s.totalAllocatedMB);
  const totalUsed = num(s.totalUsedMB);
  const dbAllocated = num(s.databaseAllocatedMB);
  const dbFree = num(s.databaseFreeMB);
  const fileStorage = num(s.fileStorageMB);
  const filesTotalSizeMB = num(d.files?.totalSizeMB);
  const largestFolderEntry = folders.reduce((b, f) => (!b || num(f.sizeMB) > num(b.sizeMB) ? f : b), null);

  const row = {
    code,
    name: s.clientName || code,
    dbName: db.databaseName || '—',
    state: props.state ?? '—',
    dbAllocated,
    dbUsed: num(s.databaseUsedMB),
    dbFree,
    dbFreePct: pctOf(dbFree, dbAllocated),
    dataPct: filePct(dataFile),
    logPct: filePct(logFile),
    files: fileStorage,
    filesTotalSizeMB,
    // summary.fileStorageMB and files.totalSizeMB describe the same thing two
    // different ways in the response — surface it if they ever disagree.
    filesMismatch: Math.abs(fileStorage - filesTotalSizeMB) > 0.01,
    fileCount: folders.reduce((n, f) => n + num(f.fileCount), 0),
    activeFolders: folders.filter((f) => num(f.fileCount) > 0).length,
    emptyFolders: folders.filter((f) => num(f.fileCount) === 0).length,
    folderCount: folders.length,
    largestFolder: largestFolderEntry && num(largestFolderEntry.sizeMB) > 0 ? largestFolderEntry.folderName : '—',
    totalAllocated,
    totalUsed,
    totalPct: pctOf(totalUsed, totalAllocated),
    calculatedOn: s.calculatedOn,
    recoveryModel: props.recoveryModel ?? '—',
    compatibilityLevel: props.compatibilityLevel ?? '—',
    collation: props.collation ?? '—',
    autoGrowth: props.autoGrowth ?? '—',
    createDate: props.createDate ?? null,
    isReadOnly: !!props.isReadOnly,
    autoClose: !!props.autoClose,
    autoShrink: !!props.autoShrink,
  };
  row.flags = healthFlags(row);
  row.flagCount = row.flags.length;
  return row;
}

// ---------------------------------------------------------------------------
// small components
// ---------------------------------------------------------------------------
function Usage({ pct }) {
  const p = num(pct);
  return (
    <div className="db-usage">
      <span className="db-usage__track"><i className={`lv-${lv(p)}`} style={{ width: `${Math.min(100, p)}%` }} /></span>
      <span className={`db-usage__pct lv-${lv(p)}`}>{fmtPct(p)}</span>
    </div>
  );
}

function Flags({ flags }) {
  if (!flags.length) return <span className="db-ok">ok</span>;
  return (
    <div className="db-flags">
      {flags.map((f) => <span key={f.t} className={`db-flag lv-${f.lvl}`}>{f.t}</span>)}
    </div>
  );
}

function StateBadge({ state }) {
  const ok = String(state).toUpperCase() === 'ONLINE';
  return <span className={`db-flag ${ok ? 'lv-lo' : 'lv-hi'}`}>{state}</span>;
}

function SortTh({ label, k, sort, onSort, className = '' }) {
  const active = sort.key === k;
  return (
    <th className={`db-sort ${className}${active ? ' is-active' : ''}`} onClick={() => onSort(k)} title="click to sort">
      {label}{active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

// Multi-select dropdown: search, select all / unselect all, add-by-code.
// The popover is position:fixed so a Panel with overflow:hidden can't clip it.
function ClientPicker({ codes, selected, onChange, onAdd, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [newCode, setNewCode] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 340 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.max(r.width, 340);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    setPos({ top: r.bottom + 6, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  const needle = q.trim().toLowerCase();
  const filtered = needle ? codes.filter((c) => c.toLowerCase().includes(needle)) : codes;
  const allOn = filtered.length > 0 && filtered.every((c) => selected.has(c));
  const anyOn = filtered.some((c) => selected.has(c));

  const toggle = (c) => {
    const next = new Set(selected);
    if (next.has(c)) next.delete(c); else next.add(c);
    onChange(next);
  };
  const selectAll = () => onChange(new Set([...selected, ...filtered]));
  const unselectAll = () => onChange(new Set([...selected].filter((c) => !filtered.includes(c))));

  const submitNew = () => {
    const v = newCode.trim();
    if (!v) return;
    onAdd(v);
    setNewCode('');
  };

  return (
    <>
      <label className="db-field" style={{ width: 260 }}>
        <span className="db-field__label">clients</span>
        <button
          ref={btnRef}
          type="button"
          className="db-select db-pick__btn"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span className={selected.size ? '' : 'db-dim'}>
            {selected.size ? `${selected.size} of ${codes.length} selected` : 'none selected'}
          </span>
          <span className="db-pick__caret">{open ? '▴' : '▾'}</span>
        </button>
      </label>

      {open && (
        <div ref={panelRef} className="db-pick__panel" style={{ top: pos.top, left: pos.left, width: pos.width }}>
          <div className="db-pick__head">
            <input
              className="db-input db-pick__search"
              autoFocus
              placeholder="filter clients..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="db-pick__bulk">
            <button type="button" className="term-btn term-btn--sm" disabled={allOn || !filtered.length} onClick={selectAll}>
              select_all{needle ? ' (filtered)' : ''}
            </button>
            <button type="button" className="term-btn term-btn--sm term-btn--ghost" disabled={!anyOn} onClick={unselectAll}>
              unselect_all{needle ? ' (filtered)' : ''}
            </button>
          </div>

          <div className="db-pick__list" role="listbox" aria-multiselectable="true">
            {filtered.length === 0 && (
              <div className="db-pick__empty">{codes.length ? 'no client matches the filter' : 'no clients loaded — add one below'}</div>
            )}
            {filtered.map((c) => (
              <label key={c} className="db-pick__item">
                <input type="checkbox" checked={selected.has(c)} onChange={() => toggle(c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>

          <div className="db-pick__foot">
            <input
              className="db-input db-pick__search"
              placeholder="add client code, press Enter"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitNew(); }}
            />
            <button type="button" className="term-btn term-btn--sm" disabled={!newCode.trim()} onClick={submitNew}>add</button>
          </div>
        </div>
      )}
    </>
  );
}

const CSS = `
.db-root{--db-heat:57,255,120;--db-line:var(--border,#17683a);--db-dim:var(--text-dim,#5f8f72);--db-bg:var(--panel,#060d0a);--db-solid:#050a08}
.db-toolbar{display:flex;flex-wrap:wrap;gap:14px;align-items:center;margin-bottom:14px}
.db-toolbar--end{margin-left:auto}
.db-dim{color:var(--db-dim)}
.db-hint{color:var(--db-dim);font-size:11.5px;margin:0}
.db-ok{color:var(--green,#3dff7f)}
.db-err{color:var(--red,#ff5c5c)}
.db-run{color:var(--amber,#ffb454)}

/* fields */
.db-field{position:relative;display:inline-block}
.db-field__label{position:absolute;top:-8px;left:10px;z-index:1;padding:0 4px;font-size:10.5px;line-height:1;color:var(--green,#3dff7f);background:var(--db-bg)}
.db-input,.db-select{width:100%;box-sizing:border-box;padding:11px 10px 8px;font:inherit;font-size:13.5px;color:var(--text,#d7ffe6);background:transparent;border:1px solid var(--db-line);border-radius:4px}
.db-input:focus,.db-select:focus{outline:none;border-color:var(--green,#3dff7f)}
.db-input:disabled,.db-select:disabled{opacity:.45}
.db-select option{background:var(--db-solid);color:#d7ffe6}
.db-check{display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--text,#d7ffe6);cursor:pointer}

/* client dropdown */
.db-pick__btn{display:flex;align-items:center;justify-content:space-between;text-align:left;cursor:pointer}
.db-pick__caret{color:var(--green,#3dff7f)}
.db-pick__panel{position:fixed;z-index:1000;background:var(--db-solid);border:1px solid var(--green,#3dff7f);border-radius:4px;box-shadow:0 12px 32px rgba(0,0,0,.6)}
.db-pick__head{padding:10px 10px 0}
.db-pick__search{padding:8px 10px}
.db-pick__bulk{display:flex;gap:8px;padding:10px;border-bottom:1px solid var(--db-line)}
.db-pick__list{max-height:250px;overflow:auto}
.db-pick__item{display:flex;align-items:center;gap:10px;padding:7px 12px;font-size:12.5px;cursor:pointer}
.db-pick__item:hover{background:rgba(var(--db-heat),.08)}
.db-pick__empty{padding:14px 12px;font-size:12px;color:var(--db-dim)}
.db-pick__foot{display:flex;gap:8px;padding:10px;border-top:1px solid var(--db-line)}

/* chips */
.db-chips{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:14px}
.db-chip{font:inherit;font-size:12px;padding:3px 9px;color:var(--green,#3dff7f);background:rgba(var(--db-heat),.08);border:1px solid var(--green,#3dff7f);border-radius:3px;cursor:pointer}
.db-chip:disabled{cursor:not-allowed;opacity:.5}

/* scan log */
.db-term{font-size:12px;line-height:1.75;padding:10px 14px;background:rgba(0,0,0,.35);border:1px solid var(--db-line);border-radius:4px;overflow-x:auto;white-space:pre}
.db-q{color:var(--db-dim)}

/* stats */
.db-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px;margin-bottom:16px}
.db-stat{padding:10px 12px;border:1px solid var(--db-line);border-radius:4px}
.db-stat__k{font-size:10.5px;color:var(--db-dim)}
.db-stat__v{margin-top:4px;font-size:20px;color:var(--green,#3dff7f);font-variant-numeric:tabular-nums}
.db-stat__v--sm{font-size:14px;line-height:28px}

/* tabs */
.db-tabs{display:flex;gap:4px;margin:18px 0 12px;border-bottom:1px solid var(--db-line);overflow-x:auto}
.db-tab{flex:0 0 auto;font:inherit;font-size:12.5px;padding:8px 14px;color:var(--db-dim);background:transparent;border:0;border-bottom:2px solid transparent;cursor:pointer}
.db-tab.on{color:var(--green,#3dff7f);border-bottom-color:var(--green,#3dff7f)}

/* tables */
.db-tablewrap{max-height:65vh;overflow:auto;border:1px solid var(--db-line);border-radius:4px}
.db-tablewrap--free{max-height:none}
.db-tbl{width:100%;border-collapse:separate;border-spacing:0;font-size:12.5px}
.db-tbl th,.db-tbl td{padding:7px 12px;text-align:left;white-space:nowrap;border-bottom:1px solid rgba(var(--db-heat),.08)}
.db-tbl thead th{position:sticky;top:0;z-index:3;background:var(--db-solid);color:var(--green,#3dff7f);font-weight:600;border-bottom:1px solid var(--db-line)}
.db-tbl thead tr.grp th{height:26px;box-sizing:border-box;padding:4px 12px;font-size:10.5px;font-weight:400;color:var(--db-dim);text-align:center;border-bottom:1px dashed var(--db-line);border-left:1px solid var(--db-line)}
.db-tbl thead tr.grp th:first-child{border-left:0}
.db-tbl thead tr.cols th{top:26px}
.db-tbl tbody tr:nth-child(even) td{background-color:rgba(var(--db-heat),.025)}
.db-tbl tbody tr:hover td{background-color:rgba(var(--db-heat),.09)}
.db-tbl tbody tr.first td{border-top:1px solid var(--db-line)}
.db-tbl tfoot td{position:sticky;bottom:0;z-index:3;background:var(--db-solid);border-top:1px solid var(--db-line);font-weight:700;color:var(--green,#3dff7f)}
.db-tbl tfoot tr+tr td{bottom:0}
.db-tbl .r{text-align:right;font-variant-numeric:tabular-nums}
.db-tbl .sep{border-left:1px solid var(--db-line)}
.db-tbl.sticky-first th:first-child,.db-tbl.sticky-first td:first-child{position:sticky;left:0;z-index:2;background-color:var(--db-solid);border-right:1px solid var(--db-line)}
.db-tbl.sticky-first thead th:first-child{z-index:4}
.db-tbl.sticky-first tfoot td:first-child{z-index:4}
.db-tbl td.is-diff{background-color:rgba(255,180,84,.14)!important}
.db-tbl td.is-bad{background-color:rgba(255,92,92,.14)!important}
.db-sort{cursor:pointer;user-select:none}
.db-sort:hover,.db-sort.is-active{color:var(--amber,#ffb454)!important}
.db-sub{font-size:10.5px;color:var(--db-dim)}
.db-zero{color:rgba(var(--db-heat),.3)}

/* usage bar + flags */
.lv-lo{color:var(--green,#3dff7f)}
.lv-mid{color:var(--amber,#ffb454)}
.lv-hi{color:var(--red,#ff5c5c)}
.db-usage{display:flex;align-items:center;gap:8px;justify-content:flex-end}
.db-usage__track{flex:0 0 80px;height:7px;background:rgba(var(--db-heat),.10);border-radius:3px;overflow:hidden}
.db-usage__track>i{display:block;height:100%}
.db-usage__track>i.lv-lo{background:var(--green,#3dff7f)}
.db-usage__track>i.lv-mid{background:var(--amber,#ffb454)}
.db-usage__track>i.lv-hi{background:var(--red,#ff5c5c)}
.db-usage__pct{min-width:48px;text-align:right}
.db-flags{display:flex;gap:6px;flex-wrap:wrap}
.db-flag{display:inline-block;padding:1px 8px;font-size:11px;border:1px solid currentColor;border-radius:10px;white-space:nowrap}

/* detail */
.db-h{margin:22px 0 8px;font-size:12px;font-weight:600;letter-spacing:.06em;color:var(--green,#3dff7f)}
.db-h span{margin-left:10px;font-weight:400;letter-spacing:0;color:var(--db-dim)}
.db-kv{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));border:1px solid var(--db-line);border-radius:4px}
.db-kv__row{display:flex;justify-content:space-between;gap:12px;padding:8px 12px;font-size:12.5px;border-bottom:1px solid rgba(var(--db-heat),.08)}
.db-kv__k{color:var(--db-dim)}
.db-raw{border:1px solid var(--db-line);border-radius:4px}
.db-raw summary{padding:8px 12px;font-size:12px;color:var(--green,#3dff7f);cursor:pointer}
.db-raw__pre{margin:0;padding:12px;max-height:420px;overflow:auto;font-size:11.5px;line-height:1.5;color:var(--text,#d7ffe6);border-top:1px solid var(--db-line);background:rgba(0,0,0,.35)}
.db-legend{display:flex;flex-wrap:wrap;gap:6px 18px;margin:10px 0 0;font-size:11.5px;color:var(--db-dim)}
.db-swatch{display:inline-block;width:10px;height:10px;margin-right:6px;vertical-align:-1px;border-radius:2px}
@media (max-width:700px){.db-toolbar--end{margin-left:0}}

/* compact all-fields cards */
.db-cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(370px,1fr));gap:14px;align-items:start}
.db-card{border:1px solid var(--db-line);border-radius:6px;padding:12px 14px;font-size:12px;background:rgba(var(--db-heat),.015)}
.db-card__head{display:flex;justify-content:space-between;align-items:baseline;gap:8px;font-size:13.5px}
.db-card__flags{margin-top:5px}
.db-card__stats{display:flex;flex-wrap:wrap;gap:5px 12px;margin:10px 0 2px;font-size:11.5px;font-variant-numeric:tabular-nums}
.db-card__section{margin:11px 0 4px;font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--green,#3dff7f)}
.db-card__section span{margin-left:8px;font-weight:400;letter-spacing:0;text-transform:none;color:var(--db-dim)}
.db-card__tbl{width:100%;border-collapse:collapse;font-size:11px}
.db-card__tbl th,.db-card__tbl td{padding:3px 6px;text-align:left;white-space:nowrap;border-bottom:1px solid rgba(var(--db-heat),.06)}
.db-card__tbl th{color:var(--db-dim);font-weight:500}
.db-card__tbl .r{text-align:right;font-variant-numeric:tabular-nums}
.db-card__tbl-wrap{overflow-x:auto}
.db-card__props{display:flex;flex-wrap:wrap;gap:3px 14px;font-size:11px}
.db-card__props b{color:var(--db-dim);font-weight:500;margin-right:3px}
.db-card__raw{margin-top:10px}
.db-card__raw summary{font-size:11px;padding:4px 0;color:var(--green,#3dff7f);cursor:pointer}
.db-card__raw .db-raw__pre{max-height:260px;font-size:10.5px;padding:8px;margin-top:4px;border:1px solid var(--db-line);border-radius:4px;background:rgba(0,0,0,.35)}
`;

// preferred display order for databaseProperties; unknown extras are appended
const PROP_ORDER = [
  'state', 'recoveryModel', 'compatibilityLevel', 'collation', 'createDate',
  'isReadOnly', 'autoClose', 'autoShrink', 'autoGrowth',
];
const PROP_NEVER_UNIFORM = new Set(['createDate']); // naturally differs per client

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------
export default function DbSizesMonitoring() {
  const toast = useToast();
  const [tab, setTab] = useState('compact'); // compact | overview | dbfiles | folders | props | tables | detail

  // ---- targets (nothing selected initially) ----
  const [codes, setCodes] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [tenantsLoading, setTenantsLoading] = useState(true);

  // ---- scan (one client at a time) ----
  const [scanning, setScanning] = useState(false);
  const [scanOrder, setScanOrder] = useState([]);
  const [scanStatus, setScanStatus] = useState({});
  const [results, setResults] = useState({}); // code -> response.data
  const [rawResp, setRawResp] = useState({}); // code -> full response
  const scanSeq = useRef(0);

  // ---- view options ----
  const [sort, setSort] = useState({ key: 'totalUsed', dir: 'desc' });
  const [hideEmptyFolders, setHideEmptyFolders] = useState(true);
  const [detailClient, setDetailClient] = useState('');

  useEffect(() => () => { scanSeq.current += 1; }, []); // stop scan on unmount

  // ---- tenants -> dropdown options (no auto-selection) ----
  useEffect(() => {
    (async () => {
      try {
        const res = await tenantApi.getAllTenants();
        const list = [...new Set(toList(res).map(codeOf).filter(Boolean))];
        setCodes((prev) => [...new Set([...list, ...prev])]);
      } catch (err) {
        toast.error(`Could not load tenants: ${errText(err)} — add client codes manually in the dropdown.`);
      } finally {
        setTenantsLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const addCode = (v) => {
    setCodes((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setSelected((s) => new Set(s).add(v));
  };
  const removeSelected = (c) =>
    setSelected((s) => {
      const next = new Set(s);
      next.delete(c);
      return next;
    });

  // ---- run / abort scan ----
  const runScan = async () => {
    const targets = codes.filter((c) => selected.has(c));
    if (!targets.length) {
      toast.error('Select at least one client first.');
      return;
    }
    const seq = ++scanSeq.current;
    const isCancelled = () => seq !== scanSeq.current;

    setScanning(true);
    setResults({});
    setRawResp({});
    setScanOrder(targets);
    setScanStatus(Object.fromEntries(targets.map((c) => [c, { state: 'queued' }])));

    for (const code of targets) {
      if (isCancelled()) break;
      setScanStatus((s) => ({ ...s, [code]: { state: 'running' } }));
      try {
        const res = await tenantApi.getStorageInfo(code);
        if (isCancelled()) break;
        const data = unwrap(res);
        setResults((r) => ({ ...r, [code]: data }));
        setRawResp((r) => ({ ...r, [code]: res }));
        const row = toRow(code, data);
        setScanStatus((s) => ({
          ...s,
          [code]: { state: 'done', msg: `used ${mb(row.totalUsed)} / ${mb(row.totalAllocated)} MB (${fmtPct(row.totalPct)})${row.flagCount ? `  ⚠ ${row.flagCount} flag(s)` : ''}` },
        }));
      } catch (err) {
        if (isCancelled()) break;
        setScanStatus((s) => ({ ...s, [code]: { state: 'error', msg: errText(err) } }));
      }
    }
    if (!isCancelled()) setScanning(false);
  };

  const abortScan = () => {
    scanSeq.current += 1;
    setScanning(false);
    setScanStatus((s) => {
      const next = { ...s };
      Object.keys(next).forEach((k) => {
        if (next[k].state === 'running' || next[k].state === 'queued') next[k] = { state: 'aborted' };
      });
      return next;
    });
  };

  // ---- derived: overview rows ----
  const scanned = useMemo(() => scanOrder.filter((c) => results[c]), [scanOrder, results]);
  const rows = useMemo(() => scanned.map((c) => toRow(c, results[c])), [scanned, results]);

  const sortedRows = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
  }, [rows, sort]);

  const STRING_KEYS = ['code', 'dbName', 'state', 'recoveryModel', 'collation', 'autoGrowth', 'largestFolder'];
  const onSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: STRING_KEYS.includes(key) ? 'asc' : 'desc' }));

  const totals = useMemo(() => {
    const t = rows.reduce(
      (acc, r) => ({
        dbAllocated: acc.dbAllocated + r.dbAllocated,
        dbUsed: acc.dbUsed + r.dbUsed,
        dbFree: acc.dbFree + r.dbFree,
        files: acc.files + r.files,
        fileCount: acc.fileCount + r.fileCount,
        totalAllocated: acc.totalAllocated + r.totalAllocated,
        totalUsed: acc.totalUsed + r.totalUsed,
      }),
      { dbAllocated: 0, dbUsed: 0, dbFree: 0, files: 0, fileCount: 0, totalAllocated: 0, totalUsed: 0 }
    );
    return { ...t, pct: pctOf(t.totalUsed, t.totalAllocated), dbFreePct: pctOf(t.dbFree, t.dbAllocated) };
  }, [rows]);

  const fullest = useMemo(() => rows.reduce((b, r) => (!b || r.totalPct > b.totalPct ? r : b), null), [rows]);
  const biggest = useMemo(() => rows.reduce((b, r) => (!b || r.totalUsed > b.totalUsed ? r : b), null), [rows]);
  const attention = rows.filter((r) => r.flags.some((f) => f.lvl === 'hi')).length;

  // ---- derived: db_files (data + log file per client) ----
  const fileRows = useMemo(
    () =>
      sortedRows.flatMap((r) => {
        const db = results[r.code]?.database || {};
        return [['data', db.dataFile], ['log', db.logFile]]
          .filter(([, f]) => f)
          .map(([kind, f], i) => ({ code: r.code, kind, first: i === 0, f }));
      }),
    [sortedRows, results]
  );
  const fileTotals = useMemo(() => {
    const t = { data: { a: 0, u: 0, f: 0 }, log: { a: 0, u: 0, f: 0 } };
    fileRows.forEach(({ kind, f }) => {
      t[kind].a += num(f.allocatedMB);
      t[kind].u += num(f.usedMB);
      t[kind].f += num(f.freeMB);
    });
    return t;
  }, [fileRows]);

  // ---- derived: folders (matrix + per-folder rollup) ----
  const folders = useMemo(() => {
    const map = new Map();
    scanned.forEach((c) =>
      (results[c].files?.folders || []).forEach((f) => {
        let e = map.get(f.folderName);
        if (!e) {
          e = { name: f.folderName, cells: {}, size: 0, count: 0, active: 0, largest: { v: 0, c: '' }, last: null };
          map.set(f.folderName, e);
        }
        const cell = {
          size: num(f.sizeMB),
          count: num(f.fileCount),
          largest: num(f.largestFileMB),
          avg: num(f.averageFileMB),
          last: f.lastModified,
        };
        e.cells[c] = cell;
        e.size += cell.size;
        e.count += cell.count;
        if (cell.count > 0) e.active += 1;
        if (cell.largest > e.largest.v) e.largest = { v: cell.largest, c };
        if (f.lastModified && (!e.last || new Date(f.lastModified) > new Date(e.last.t))) e.last = { t: f.lastModified, c };
      })
    );
    const all = [...map.values()].sort((a, b) => b.size - a.size || a.name.localeCompare(b.name));
    const empty = all.filter((r) => r.count === 0 && r.size === 0).length;
    const shown = hideEmptyFolders ? all.filter((r) => r.count > 0 || r.size > 0) : all;
    let max = 0;
    shown.forEach((r) => Object.values(r.cells).forEach((c) => { if (c.size > max) max = c.size; }));
    const colTotals = {};
    scanned.forEach((c) => {
      colTotals[c] = shown.reduce(
        (acc, r) => ({ size: acc.size + (r.cells[c]?.size || 0), count: acc.count + (r.cells[c]?.count || 0) }),
        { size: 0, count: 0 }
      );
    });
    return {
      rows: shown,
      empty,
      max,
      colTotals,
      grandSize: shown.reduce((s, r) => s + r.size, 0),
      grandCount: shown.reduce((s, r) => s + r.count, 0),
    };
  }, [scanned, results, hideEmptyFolders]);

  // ---- derived: database properties matrix ----
  const propRows = useMemo(() => {
    const has = (k) => scanned.some((c) => results[c].databaseProperties && k in results[c].databaseProperties);
    const keys = PROP_ORDER.filter(has);
    scanned.forEach((c) =>
      Object.keys(results[c].databaseProperties || {}).forEach((k) => { if (!keys.includes(k)) keys.push(k); })
    );
    return keys.map((k) => {
      const vals = scanned.map((c) => results[c].databaseProperties?.[k]);
      const counts = new Map();
      vals.forEach((v) => {
        const s = JSON.stringify(v ?? null);
        counts.set(s, (counts.get(s) || 0) + 1);
      });
      let modeKey = null;
      let modeN = 0;
      counts.forEach((n, s) => { if (n > modeN) { modeN = n; modeKey = s; } });
      return { key: k, vals, modeKey, uniform: counts.size === 1 };
    });
  }, [scanned, results]);

  // ---- derived: tables from every client (shape not fixed by the API) ----
  const allTables = useMemo(
    () =>
      scanned.flatMap((c) =>
        (Array.isArray(results[c].tables) ? results[c].tables : []).map((t) => ({
          __client: c,
          ...(t && typeof t === 'object' ? t : { value: t }),
        }))
      ),
    [scanned, results]
  );
  const tableCols = useMemo(
    () => [...new Set(allTables.flatMap((t) => Object.keys(t).filter((k) => k !== '__client')))],
    [allTables]
  );

  const activeDetail = scanned.includes(detailClient) ? detailClient : scanned[0] || '';
  const detail = activeDetail ? results[activeDetail] : null;
  const detailRow = activeDetail ? rows.find((r) => r.code === activeDetail) : null;
  const selList = codes.filter((c) => selected.has(c));
  const doneCount = scanOrder.filter((c) => ['done', 'error', 'aborted'].includes(scanStatus[c]?.state)).length;
  const noData = scanning
    ? 'scan in progress — each client appears here as soon as it finishes.'
    : 'No data yet. Choose clients in the dropdown, then run_scan.';

  const copyRaw = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(rawResp[activeDetail], null, 2));
      toast.success('Raw response copied.');
    } catch {
      toast.error('Clipboard not available in this browser context.');
    }
  };

  const renderProp = (k, v) => {
    if (v === undefined || v === null) return <span className="db-zero">·</span>;
    if (typeof v === 'boolean') return String(v);
    if (isIsoDate(v)) {
      return (
        <>
          <div>{fmtDate(v)}</div>
          <div className="db-sub">{ago(v)}</div>
        </>
      );
    }
    return String(v);
  };

  // ---- render ----
  return (
    <div className="db-root">
      <style>{CSS}</style>

      <h1 className="page-title">db_sizes</h1>
      <p className="page-sub">GET /api/AdminStorage/GetStorageInfo — database, file storage and DB settings per client</p>

      {/* ------------------------------------------------ targets */}
      <Panel title="// TARGETS">
        {tenantsLoading && <Loader label="enumerating tenants" />}

        {!tenantsLoading && (
          <>
            <div className="db-toolbar">
              <ClientPicker codes={codes} selected={selected} onChange={setSelected} onAdd={addCode} disabled={scanning} />
              {/* <button
                className="term-btn term-btn--sm"
                disabled={scanning || !codes.length || selected.size === codes.length}
                onClick={() => setSelected(new Set(codes))}
              >
                select_all
              </button>
              <button
                className="term-btn term-btn--sm term-btn--ghost"
                disabled={scanning || !selected.size}
                onClick={() => setSelected(new Set())}
              >
                unselect_all
              </button> */}

              <div className="db-toolbar--end">
                {!scanning ? (
                  <button className="term-btn term-btn--solid" disabled={!selected.size} onClick={runScan}>
                    ▶ run_scan ({selected.size})
                  </button>
                ) : (
                  <button className="term-btn" onClick={abortScan}>■ abort</button>
                )}
              </div>
            </div>

            <div className="db-chips">
              {selList.length === 0 && <span className="db-dim" style={{ fontSize: 12 }}>no clients selected — open the dropdown to choose</span>}
              {selList.slice(0, 12).map((c) => (
                <button key={c} className="db-chip" disabled={scanning} title="remove" onClick={() => removeSelected(c)}>
                  {c} ×
                </button>
              ))}
              {selList.length > 12 && <span className="db-dim" style={{ fontSize: 12 }}>+{selList.length - 12} more</span>}
            </div>

            <p className="db-hint">
              run_scan calls GetStorageInfo once per selected client, one after another, and fills the tabs below.
            </p>
          </>
        )}
      </Panel>

      {/* ------------------------------------------------ scan log */}
      {scanOrder.length > 0 && (
        <Panel title="// SCAN_LOG">
          <div className="db-term">
            <div className={scanning ? 'db-run' : 'db-ok'}>
              {`# ${bar(doneCount, scanOrder.length)} ${doneCount}/${scanOrder.length} clients`}
            </div>
            {scanOrder.map((c) => {
              const s = scanStatus[c] || { state: 'queued' };
              const name = c.padEnd(8);
              if (s.state === 'queued') return <div key={c} className="db-q">{`> ${name} queued`}</div>;
              if (s.state === 'aborted') return <div key={c} className="db-q">{`> ${name} aborted`}</div>;
              if (s.state === 'running') return <div key={c} className="db-run">{`> ${name} fetching...`}</div>;
              if (s.state === 'error') return <div key={c} className="db-err">{`> ${name} ERR  ${s.msg}`}</div>;
              return <div key={c} className="db-ok">{`> ${name} ok   ${s.msg}`}</div>;
            })}
          </div>
        </Panel>
      )}

      {/* ------------------------------------------------ tabs */}
      <div className="db-tabs">
        {[
          ['compact', 'all_fields'],
          ['overview', 'overview'],
          ['dbfiles', 'db_files'],
          ['folders', 'file_folders'],
          ['props', 'db_properties'],
          ['tables', 'tables'],
          ['detail', 'client_detail'],
        ].map(([id, label]) => (
          <button key={id} className={`db-tab${tab === id ? ' on' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {/* ------------------------------------------------ summary strip */}
      {['overview', 'dbfiles', 'folders'].includes(tab) && rows.length > 0 && (
        <div className="db-stats">
          <div className="db-stat"><div className="db-stat__k">clients_scanned</div><div className="db-stat__v">{rows.length}</div></div>
          <div className="db-stat"><div className="db-stat__k">total_allocated</div><div className="db-stat__v">{size(totals.totalAllocated)}</div></div>
          <div className="db-stat"><div className="db-stat__k">total_used</div><div className="db-stat__v">{size(totals.totalUsed)}</div></div>
          <div className="db-stat">
            <div className="db-stat__k">overall_usage</div>
            <div className={`db-stat__v lv-${lv(totals.pct)}`}>{fmtPct(totals.pct)}</div>
          </div>
          <div className="db-stat"><div className="db-stat__k">database_used</div><div className="db-stat__v">{size(totals.dbUsed)}</div></div>
          <div className="db-stat"><div className="db-stat__k">file_storage</div><div className="db-stat__v">{size(totals.files)}</div></div>
          <div className="db-stat"><div className="db-stat__k">files_stored</div><div className="db-stat__v">{totals.fileCount.toLocaleString('en-US')}</div></div>
          <div className="db-stat">
            <div className="db-stat__k">largest_client</div>
            <div className="db-stat__v db-stat__v--sm">{biggest ? `${biggest.code} (${size(biggest.totalUsed)})` : '—'}</div>
          </div>
          <div className="db-stat">
            <div className="db-stat__k">fullest_client</div>
            <div className={`db-stat__v db-stat__v--sm lv-${lv(fullest?.totalPct || 0)}`}>
              {fullest ? `${fullest.code} (${fmtPct(fullest.totalPct)})` : '—'}
            </div>
          </div>
          <div className="db-stat">
            <div className="db-stat__k">needs_attention</div>
            <div className={`db-stat__v ${attention ? 'lv-hi' : 'lv-lo'}`}>{attention}</div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------ compact / all fields */}
      {tab === 'compact' && (
        <Panel title="// ALL_FIELDS — one card per client, every response field, nothing rolled up">
          {rows.length === 0 && <EmptyState>{noData}</EmptyState>}

          {rows.length > 0 && (
            <div className="db-cards">
              {sortedRows.map((r) => {
                const d = results[r.code];
                const db = d.database || {};
                const dataFile = db.dataFile || {};
                const logFile = db.logFile || {};
                const props = d.databaseProperties || {};
                const folderList = d.files?.folders || [];
                const clientTables = Array.isArray(d.tables) ? d.tables : [];
                const tblCols = [...new Set(clientTables.flatMap((t) => Object.keys(t && typeof t === 'object' ? t : { value: t })))];

                return (
                  <div className="db-card" key={r.code}>
                    <div className="db-card__head">
                      <div>
                        <b>{r.code}</b>
                        {r.name !== r.code && <span className="db-dim"> · {r.name}</span>}
                        <span className="db-dim"> · {db.databaseName || '—'}</span>
                      </div>
                      <StateBadge state={r.state} />
                    </div>
                    <div className="db-card__flags"><Flags flags={r.flags} /></div>

                    {/* summary */}
                    <div className="db-card__stats">
                      <span title="summary.databaseAllocatedMB">alloc {mb(r.dbAllocated)} MB</span>
                      <span title="summary.databaseUsedMB">used {mb(r.dbUsed)} MB</span>
                      <span title="summary.databaseFreeMB">free {mb(r.dbFree)} MB ({fmtPct(r.dbFreePct)})</span>
                      <span title="summary.fileStorageMB vs files.totalSizeMB" className={r.filesMismatch ? 'lv-mid' : ''}>
                        files {mb(r.files)} MB{r.filesMismatch ? ` (files.totalSizeMB=${mb(r.filesTotalSizeMB)})` : ''}
                      </span>
                      <span title="summary.totalAllocatedMB">total alloc {mb(r.totalAllocated)} MB</span>
                      <span title="summary.totalUsedMB">total used {mb(r.totalUsed)} MB</span>
                      <span className={`lv-${lv(r.totalPct)}`} title="totalUsed / totalAllocated">total usage {fmtPct(r.totalPct)}</span>
                      <span className="db-dim" title="summary.calculatedOn">calculated {fmtDate(r.calculatedOn)} ({ago(r.calculatedOn)})</span>
                    </div>

                    {/* database files */}
                    <div className="db-card__section">database files</div>
                    <div className="db-card__tbl-wrap">
                      <table className="db-card__tbl">
                        <thead>
                          <tr><th>file</th><th>type</th><th>logical name</th><th className="r">alloc MB</th><th className="r">used MB</th><th className="r">free MB</th><th className="r">%</th></tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>data</td><td className="db-dim">{dataFile.fileType ?? '—'}</td><td className="db-dim">{dataFile.logicalName ?? '—'}</td>
                            <td className="r">{mb(dataFile.allocatedMB)}</td><td className="r">{mb(dataFile.usedMB)}</td><td className="r">{mb(dataFile.freeMB)}</td>
                            <td className={`r lv-${lv(num(dataFile.usagePercent))}`}>{fmtPct(dataFile.usagePercent)}</td>
                          </tr>
                          <tr>
                            <td>log</td><td className="db-dim">{logFile.fileType ?? '—'}</td><td className="db-dim">{logFile.logicalName ?? '—'}</td>
                            <td className="r">{mb(logFile.allocatedMB)}</td><td className="r">{mb(logFile.usedMB)}</td><td className="r">{mb(logFile.freeMB)}</td>
                            <td className={`r lv-${lv(num(logFile.usagePercent))}`}>{fmtPct(logFile.usagePercent)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* folders — every one, including empty */}
                    <div className="db-card__section">folders<span>{folderList.length} total · {folderList.filter((f) => num(f.fileCount) > 0).length} in use · files.totalSizeMB = {mb(d.files?.totalSizeMB)} MB</span></div>
                    {folderList.length === 0 ? (
                      <div className="db-dim" style={{ fontSize: 11.5 }}>no folders returned</div>
                    ) : (
                      <div className="db-card__tbl-wrap">
                        <table className="db-card__tbl">
                          <thead>
                            <tr><th>folder</th><th className="r">files</th><th className="r">size MB</th><th className="r">largest MB</th><th className="r">avg MB</th><th>last modified</th></tr>
                          </thead>
                          <tbody>
                            {folderList.map((f) => (
                              <tr key={f.folderName} style={num(f.fileCount) === 0 ? { opacity: 0.5 } : undefined}>
                                <td>{f.folderName}</td>
                                <td className="r">{f.fileCount}</td>
                                <td className="r">{mb(f.sizeMB)}</td>
                                <td className="r">{mb(f.largestFileMB)}</td>
                                <td className="r">{mb(f.averageFileMB)}</td>
                                <td className="db-sub">{f.lastModified ? `${fmtDate(f.lastModified)} (${ago(f.lastModified)})` : 'never'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* database properties — every key returned */}
                    <div className="db-card__section">database properties</div>
                    {Object.keys(props).length === 0 ? (
                      <div className="db-dim" style={{ fontSize: 11.5 }}>none returned</div>
                    ) : (
                      <div className="db-card__props">
                        {Object.entries(props).map(([k, v]) => (
                          <span key={k}>
                            <b>{humanKey(k)}:</b>
                            {typeof v === 'boolean' ? (v ? <span className="lv-mid">true</span> : 'false') : isIsoDate(v) ? fmtDate(v) : String(v ?? '—')}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* tables — whatever the API returned, even empty */}
                    <div className="db-card__section">tables<span>{clientTables.length} returned</span></div>
                    {clientTables.length === 0 ? (
                      <div className="db-dim" style={{ fontSize: 11.5 }}>tables array is empty for this client</div>
                    ) : (
                      <div className="db-card__tbl-wrap">
                        <table className="db-card__tbl">
                          <thead><tr>{tblCols.map((k) => <th key={k}>{humanKey(k)}</th>)}</tr></thead>
                          <tbody>
                            {clientTables.map((t, i) => {
                              const o = t && typeof t === 'object' ? t : { value: t };
                              return (
                                <tr key={i}>
                                  {tblCols.map((k) => (
                                    <td key={k} className={typeof o[k] === 'number' ? 'r' : ''}>
                                      {typeof o[k] === 'number' ? o[k].toLocaleString('en-US') : isIsoDate(o[k]) ? fmtDate(o[k]) : String(o[k] ?? '—')}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* raw response, per client */}
                    <details className="db-card__raw">
                      <summary>raw JSON for {r.code}</summary>
                      <pre className="db-raw__pre">{JSON.stringify(rawResp[r.code], null, 2)}</pre>
                    </details>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      )}

      {/* ------------------------------------------------ overview */}
      {tab === 'overview' && (
        <Panel title="// STORAGE_OVERVIEW">
          {rows.length === 0 && <EmptyState>{noData}</EmptyState>}

          {rows.length > 0 && (
            <>
              <div className="db-tablewrap">
                <table className="db-tbl sticky-first">
                  <thead>
                    <tr className="grp">
                      <th colSpan={3}>tenant</th>
                      <th colSpan={6}>database</th>
                      <th colSpan={4}>file storage</th>
                      <th colSpan={3}>total (database + files)</th>
                      <th colSpan={4}>settings</th>
                      <th>health</th>
                      <th>snapshot</th>
                    </tr>
                    <tr className="cols">
                      <SortTh label="client" k="code" sort={sort} onSort={onSort} />
                      <SortTh label="database" k="dbName" sort={sort} onSort={onSort} />
                      <SortTh label="state" k="state" sort={sort} onSort={onSort} />
                      <SortTh label="allocated MB" k="dbAllocated" sort={sort} onSort={onSort} className="r sep" />
                      <SortTh label="used MB" k="dbUsed" sort={sort} onSort={onSort} className="r" />
                      <SortTh label="free MB" k="dbFree" sort={sort} onSort={onSort} className="r" />
                      <SortTh label="free %" k="dbFreePct" sort={sort} onSort={onSort} className="r" />
                      <SortTh label="data file %" k="dataPct" sort={sort} onSort={onSort} className="r" />
                      <SortTh label="log file %" k="logPct" sort={sort} onSort={onSort} className="r" />
                      <SortTh label="size MB" k="files" sort={sort} onSort={onSort} className="r sep" />
                      <SortTh label="files" k="fileCount" sort={sort} onSort={onSort} className="r" />
                      <SortTh label="folders in use" k="activeFolders" sort={sort} onSort={onSort} className="r" />
                      <SortTh label="largest folder" k="largestFolder" sort={sort} onSort={onSort} />
                      <SortTh label="allocated MB" k="totalAllocated" sort={sort} onSort={onSort} className="r sep" />
                      <SortTh label="used MB" k="totalUsed" sort={sort} onSort={onSort} className="r" />
                      <SortTh label="usage" k="totalPct" sort={sort} onSort={onSort} className="r" />
                      <SortTh label="recovery model" k="recoveryModel" sort={sort} onSort={onSort} className="sep" />
                      <SortTh label="compat. level" k="compatibilityLevel" sort={sort} onSort={onSort} className="r" />
                      <SortTh label="collation" k="collation" sort={sort} onSort={onSort} />
                      <SortTh label="auto growth" k="autoGrowth" sort={sort} onSort={onSort} />
                      <SortTh label="flags" k="flagCount" sort={sort} onSort={onSort} className="sep" />
                      <th className="sep">calculated on</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r) => (
                      <tr key={r.code}>
                        <td>
                          <b>{r.code}</b>
                          {r.name !== r.code && <div className="db-sub">{r.name}</div>}
                        </td>
                        <td className="db-dim">{r.dbName}</td>
                        <td><StateBadge state={r.state} /></td>
                        <td className="r sep">{mb(r.dbAllocated)}</td>
                        <td className="r">{mb(r.dbUsed)}</td>
                        <td className="r">{mb(r.dbFree)}</td>
                        <td className="r">{fmtPct(r.dbFreePct)}</td>
                        <td className={`r lv-${lv(r.dataPct)}`}>{fmtPct(r.dataPct)}</td>
                        <td className={`r lv-${lv(r.logPct)}`}>{fmtPct(r.logPct)}</td>
                        <td
                          className={`r sep${r.filesMismatch ? ' is-diff' : ''}`}
                          title={r.filesMismatch ? `files.totalSizeMB reports ${mb(r.filesTotalSizeMB)} MB` : undefined}
                        >
                          {mb(r.files)}
                        </td>
                        <td className="r">{r.fileCount.toLocaleString('en-US')}</td>
                        <td className="r">{r.activeFolders}/{r.folderCount}</td>
                        <td className="db-dim">{r.largestFolder}</td>
                        <td className="r sep">{mb(r.totalAllocated)}</td>
                        <td className="r">{mb(r.totalUsed)}</td>
                        <td className="r"><Usage pct={r.totalPct} /></td>
                        <td className="db-dim sep">{r.recoveryModel}</td>
                        <td className="r">{r.compatibilityLevel}</td>
                        <td className="db-dim" title={r.collation}>{r.collation}</td>
                        <td className="db-dim">{r.autoGrowth}</td>
                        <td className="sep"><Flags flags={r.flags} /></td>
                        <td className="sep">
                          <div>{fmtDate(r.calculatedOn)}</div>
                          <div className="db-sub">{ago(r.calculatedOn)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>total</td>
                      <td />
                      <td />
                      <td className="r sep">{mb(totals.dbAllocated)}</td>
                      <td className="r">{mb(totals.dbUsed)}</td>
                      <td className="r">{mb(totals.dbFree)}</td>
                      <td className="r">{fmtPct(totals.dbFreePct)}</td>
                      <td />
                      <td />
                      <td className="r sep">{mb(totals.files)}</td>
                      <td className="r">{totals.fileCount.toLocaleString('en-US')}</td>
                      <td />
                      <td />
                      <td className="r sep">{mb(totals.totalAllocated)}</td>
                      <td className="r">{mb(totals.totalUsed)}</td>
                      <td className="r"><Usage pct={totals.pct} /></td>
                      <td className="sep" colSpan={4} />
                      <td className="sep">{attention ? `${attention} need attention` : 'all ok'}</td>
                      <td className="sep" />
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="db-legend">
                <span>click a column header to sort</span>
                <span><span className="lv-lo">■</span> under 60% used</span>
                <span><span className="lv-mid">■</span> 60–85%</span>
                <span><span className="lv-hi">■</span> over 85%</span>
                <span>flags: state not ONLINE, read-only, any file or total over 85% full, auto-shrink / auto-close on, file size mismatch</span>
                <span>settings columns come straight from databaseProperties — the full set (incl. create date) is on db_properties and client_detail</span>
                <span><i className="db-swatch" style={{ background: 'rgba(255,180,84,.5)' }} />amber "size MB" cell = summary.fileStorageMB differs from files.totalSizeMB for that client (hover for both values)</span>
                <span>db_files, file_folders, db_properties and tables tabs break each part down further</span>
              </div>
            </>
          )}
        </Panel>
      )}

      {/* ------------------------------------------------ db files */}
      {tab === 'dbfiles' && (
        <Panel title="// DATABASE_FILES">
          {rows.length === 0 && <EmptyState>{noData}</EmptyState>}

          {rows.length > 0 && (
            <>
              <div className="db-tablewrap">
                <table className="db-tbl sticky-first">
                  <thead>
                    <tr>
                      <th>client</th>
                      <th>file</th>
                      <th>type</th>
                      <th>logical name</th>
                      <th className="r">allocated MB</th>
                      <th className="r">used MB</th>
                      <th className="r">free MB</th>
                      <th className="r">usage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fileRows.map(({ code, kind, first, f }) => (
                      <tr key={`${code}:${kind}`} className={first ? 'first' : ''}>
                        <td>{first ? <b>{code}</b> : <span className="db-zero">↳</span>}</td>
                        <td>{kind === 'data' ? 'data file' : 'log file'}</td>
                        <td className="db-dim">{f.fileType}</td>
                        <td className="db-dim">{f.logicalName}</td>
                        <td className="r">{mb(f.allocatedMB)}</td>
                        <td className="r">{mb(f.usedMB)}</td>
                        <td className="r">{mb(f.freeMB)}</td>
                        <td className="r"><Usage pct={f.usagePercent ?? pctOf(num(f.usedMB), num(f.allocatedMB))} /></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {['data', 'log'].map((kind) => (
                      <tr key={kind}>
                        <td>total</td>
                        <td>{kind} files</td>
                        <td />
                        <td />
                        <td className="r">{mb(fileTotals[kind].a)}</td>
                        <td className="r">{mb(fileTotals[kind].u)}</td>
                        <td className="r">{mb(fileTotals[kind].f)}</td>
                        <td className="r"><Usage pct={pctOf(fileTotals[kind].u, fileTotals[kind].a)} /></td>
                      </tr>
                    ))}
                  </tfoot>
                </table>
              </div>
              <div className="db-legend">
                <span>two rows per client: the ROWS (data) file and the LOG file</span>
                <span>row order follows the sort chosen on the overview tab</span>
              </div>
            </>
          )}
        </Panel>
      )}

      {/* ------------------------------------------------ folders */}
      {tab === 'folders' && (
        <Panel title="// FILE_FOLDERS">
          {rows.length === 0 && <EmptyState>{noData}</EmptyState>}

          {rows.length > 0 && (
            <>
              <div className="db-toolbar">
                <label className="db-check">
                  <input type="checkbox" checked={hideEmptyFolders} onChange={(e) => setHideEmptyFolders(e.target.checked)} />
                  hide folders with no files
                  {hideEmptyFolders && folders.empty > 0 ? ` (${folders.empty} hidden)` : ''}
                </label>
              </div>

              {folders.rows.length === 0 ? (
                <EmptyState>No files stored in any folder for the scanned clients.</EmptyState>
              ) : (
                <>
                  <div className="db-h" style={{ marginTop: 0 }}>// SIZE_BY_CLIENT<span>MB, file count underneath — hover a cell for largest / average / last modified</span></div>
                  <div className="db-tablewrap">
                    <table className="db-tbl sticky-first">
                      <thead>
                        <tr>
                          <th>folder</th>
                          {scanned.map((c) => <th key={c} className="r">{c}</th>)}
                          <th className="r sep">total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {folders.rows.map((f) => (
                          <tr key={f.name}>
                            <td><b>{f.name}</b></td>
                            {scanned.map((c) => {
                              const cell = f.cells[c];
                              if (!cell) return <td key={c} className="r"><span className="db-zero">·</span></td>;
                              if (cell.size === 0 && cell.count === 0) return <td key={c} className="r"><span className="db-zero">0</span></td>;
                              const heat = folders.max
                                ? { backgroundColor: `rgba(var(--db-heat), ${(0.06 + 0.42 * (cell.size / folders.max)).toFixed(3)})` }
                                : undefined;
                              return (
                                <td
                                  key={c}
                                  className="r"
                                  style={heat}
                                  title={`largest ${mb(cell.largest)} MB · average ${mb(cell.avg)} MB · last modified ${fmtDate(cell.last)}`}
                                >
                                  <div>{mb(cell.size)}</div>
                                  <div className="db-sub">{cell.count} file{cell.count === 1 ? '' : 's'}</div>
                                </td>
                              );
                            })}
                            <td className="r sep">
                              <div><b>{mb(f.size)}</b></div>
                              <div className="db-sub">{f.count} file{f.count === 1 ? '' : 's'}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>total</td>
                          {scanned.map((c) => <td key={c} className="r">{mb(folders.colTotals[c]?.size)}</td>)}
                          <td className="r sep">{mb(folders.grandSize)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="db-legend">
                    <span><i className="db-swatch" style={{ background: 'rgba(var(--db-heat),.5)' }} />shading = size vs. largest cell ({mb(folders.max)} MB)</span>
                    <span><span className="db-zero">·</span> = folder not reported for that client</span>
                  </div>

                  <div className="db-h">// FOLDER_ROLLUP<span>each folder across all scanned clients</span></div>
                  <div className="db-tablewrap db-tablewrap--free">
                    <table className="db-tbl">
                      <thead>
                        <tr>
                          <th>folder</th>
                          <th className="r">clients using</th>
                          <th className="r">files</th>
                          <th className="r">size MB</th>
                          <th className="r">share</th>
                          <th className="r">largest file MB</th>
                          <th className="r">average file MB</th>
                          <th>last modified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {folders.rows.map((f) => {
                          const share = pctOf(f.size, folders.grandSize);
                          return (
                            <tr key={f.name}>
                              <td><b>{f.name}</b></td>
                              <td className="r">{f.active}/{scanned.length}</td>
                              <td className="r">{f.count.toLocaleString('en-US')}</td>
                              <td className="r">{mb(f.size)}</td>
                              <td className="r">
                                <div className="db-usage">
                                  <span className="db-usage__track"><i className="lv-lo" style={{ width: `${Math.min(100, share)}%` }} /></span>
                                  <span className="db-usage__pct">{fmtPct(share)}</span>
                                </div>
                              </td>
                              <td className="r">
                                {f.largest.v ? mb(f.largest.v) : <span className="db-zero">—</span>}
                                {f.largest.v > 0 && <div className="db-sub">{f.largest.c}</div>}
                              </td>
                              <td className="r">{f.count ? mb(f.size / f.count) : <span className="db-zero">—</span>}</td>
                              <td>
                                {f.last ? (
                                  <>
                                    <div>{fmtDate(f.last.t)}</div>
                                    <div className="db-sub">{ago(f.last.t)} · {f.last.c}</div>
                                  </>
                                ) : <span className="db-zero">never</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </Panel>
      )}

      {/* ------------------------------------------------ database properties */}
      {tab === 'props' && (
        <Panel title="// DATABASE_PROPERTIES">
          {rows.length === 0 && <EmptyState>{noData}</EmptyState>}

          {rows.length > 0 && (
            <>
              <div className="db-tablewrap">
                <table className="db-tbl sticky-first">
                  <thead>
                    <tr>
                      <th>property</th>
                      {scanned.map((c) => <th key={c}>{c}</th>)}
                      <th className="sep">across clients</th>
                    </tr>
                  </thead>
                  <tbody>
                    {propRows.map((p) => (
                      <tr key={p.key}>
                        <td><b>{humanKey(p.key)}</b></td>
                        {p.vals.map((v, i) => {
                          const differs = !p.uniform && !PROP_NEVER_UNIFORM.has(p.key) && JSON.stringify(v ?? null) !== p.modeKey;
                          const bad =
                            (p.key === 'state' && v !== undefined && String(v).toUpperCase() !== 'ONLINE') ||
                            (p.key === 'isReadOnly' && v === true);
                          const cls = bad ? 'is-bad' : differs ? 'is-diff' : '';
                          const warn = (p.key === 'autoClose' || p.key === 'autoShrink') && v === true;
                          return (
                            <td key={scanned[i]} className={cls}>
                              <span className={bad ? 'lv-hi' : warn ? 'lv-mid' : ''}>{renderProp(p.key, v)}</span>
                            </td>
                          );
                        })}
                        <td className="sep">
                          {PROP_NEVER_UNIFORM.has(p.key)
                            ? <span className="db-dim">per client</span>
                            : p.uniform
                              ? <span className="db-dim">same everywhere</span>
                              : <span className="lv-mid">differs</span>}
                        </td>
                      </tr>
                    ))}
                    {propRows.length === 0 && (
                      <tr><td colSpan={scanned.length + 2} className="db-dim">no databaseProperties returned</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="db-legend">
                <span><i className="db-swatch" style={{ background: 'rgba(255,180,84,.5)' }} />value differs from what most clients use</span>
                <span><i className="db-swatch" style={{ background: 'rgba(255,92,92,.5)' }} />state not ONLINE, or database is read-only</span>
                <span>auto-close / auto-shrink on is shown in amber text</span>
              </div>
            </>
          )}
        </Panel>
      )}

      {/* ------------------------------------------------ tables */}
      {tab === 'tables' && (
        <Panel title="// TABLES">
          {rows.length === 0 && <EmptyState>{noData}</EmptyState>}

          {rows.length > 0 && (
            <>
              <p className="db-hint" style={{ marginBottom: 12 }}>
                rows returned per client:{' '}
                {scanned.map((c) => `${c}=${Array.isArray(results[c].tables) ? results[c].tables.length : 0}`).join('  ')}
              </p>

              {allTables.length === 0 ? (
                <EmptyState>None of the scanned clients returned table-level data (tables is empty).</EmptyState>
              ) : (
                <div className="db-tablewrap">
                  <table className="db-tbl">
                    <thead>
                      <tr>
                        <th>client</th>
                        {tableCols.map((k) => <th key={k}>{humanKey(k)}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {allTables.map((t, i) => (
                        <tr key={i}>
                          <td><b>{t.__client}</b></td>
                          {tableCols.map((k) => (
                            <td key={k} className={typeof t[k] === 'number' ? 'r' : ''}>
                              {typeof t[k] === 'number'
                                ? t[k].toLocaleString('en-US')
                                : isIsoDate(t[k]) ? fmtDate(t[k]) : String(t[k] ?? '—')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Panel>
      )}

      {/* ------------------------------------------------ client detail */}
      {tab === 'detail' && (
        <Panel title="// CLIENT_DETAIL">
          {rows.length === 0 && <EmptyState>{noData}</EmptyState>}

          {rows.length > 0 && detail && detailRow && (
            <>
              <div className="db-toolbar">
                <label className="db-field" style={{ width: 210 }}>
                  <span className="db-field__label">client</span>
                  <select className="db-select" value={activeDetail} onChange={(e) => setDetailClient(e.target.value)}>
                    {scanned.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <Flags flags={detailRow.flags} />
              </div>

              {/* summary — every field */}
              <div className="db-h" style={{ marginTop: 6 }}>// SUMMARY</div>
              <div className="db-kv">
                <div className="db-kv__row"><span className="db-kv__k">client code</span><span>{detail.summary?.clientCode ?? '—'}</span></div>
                <div className="db-kv__row"><span className="db-kv__k">client name</span><span>{detail.summary?.clientName ?? '—'}</span></div>
                <div className="db-kv__row"><span className="db-kv__k">database name</span><span>{detail.database?.databaseName ?? '—'}</span></div>
                <div className="db-kv__row">
                  <span className="db-kv__k">calculated on</span>
                  <span>{fmtDate(detail.summary?.calculatedOn)} <span className="db-sub">({ago(detail.summary?.calculatedOn)})</span></span>
                </div>
                <div className="db-kv__row">
                  <span className="db-kv__k">file storage (summary vs files.totalSizeMB)</span>
                  <span className={detailRow.filesMismatch ? 'lv-mid' : ''}>
                    {mb(detail.summary?.fileStorageMB)} MB / {mb(detail.files?.totalSizeMB)} MB
                    {detailRow.filesMismatch ? ' ⚠ differs' : ''}
                  </span>
                </div>
              </div>

              <div className="db-stats" style={{ marginTop: 12 }}>
                <div className="db-stat"><div className="db-stat__k">database_allocated</div><div className="db-stat__v">{size(detail.summary?.databaseAllocatedMB)}</div></div>
                <div className="db-stat"><div className="db-stat__k">database_used</div><div className="db-stat__v">{size(detail.summary?.databaseUsedMB)}</div></div>
                <div className="db-stat"><div className="db-stat__k">database_free</div><div className="db-stat__v">{size(detail.summary?.databaseFreeMB)}</div></div>
                <div className="db-stat"><div className="db-stat__k">database_free_pct</div><div className={`db-stat__v lv-${lv(100 - detailRow.dbFreePct)}`}>{fmtPct(detailRow.dbFreePct)}</div></div>
                <div className="db-stat"><div className="db-stat__k">file_storage</div><div className="db-stat__v">{size(detail.summary?.fileStorageMB)}</div></div>
                <div className="db-stat"><div className="db-stat__k">total_allocated</div><div className="db-stat__v">{size(detail.summary?.totalAllocatedMB)}</div></div>
                <div className="db-stat"><div className="db-stat__k">total_used</div><div className="db-stat__v">{size(detail.summary?.totalUsedMB)}</div></div>
                <div className="db-stat">
                  <div className="db-stat__k">total_usage</div>
                  <div className={`db-stat__v lv-${lv(detailRow.totalPct)}`}>{fmtPct(detailRow.totalPct)}</div>
                </div>
                <div className="db-stat"><div className="db-stat__k">largest_folder</div><div className="db-stat__v db-stat__v--sm">{detailRow.largestFolder}</div></div>
              </div>

              {/* database files */}
              <div className="db-h">// DATABASE_FILES</div>
              <div className="db-tablewrap db-tablewrap--free">
                <table className="db-tbl">
                  <thead>
                    <tr>
                      <th>file</th>
                      <th>type</th>
                      <th>logical name</th>
                      <th className="r">allocated MB</th>
                      <th className="r">used MB</th>
                      <th className="r">free MB</th>
                      <th className="r">usage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[['data file', detail.database?.dataFile], ['log file', detail.database?.logFile]]
                      .filter(([, f]) => f)
                      .map(([label, f]) => (
                        <tr key={label}>
                          <td><b>{label}</b></td>
                          <td>{f.fileType}</td>
                          <td className="db-dim">{f.logicalName}</td>
                          <td className="r">{mb(f.allocatedMB)}</td>
                          <td className="r">{mb(f.usedMB)}</td>
                          <td className="r">{mb(f.freeMB)}</td>
                          <td className="r"><Usage pct={f.usagePercent ?? pctOf(num(f.usedMB), num(f.allocatedMB))} /></td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* file folders */}
              <div className="db-h">// FILE_FOLDERS<span>files.totalSizeMB = {mb(detail.files?.totalSizeMB)} MB</span></div>
              {(detail.files?.folders || []).length === 0 ? (
                <EmptyState>No folder data returned.</EmptyState>
              ) : (
                <div className="db-tablewrap db-tablewrap--free">
                  <table className="db-tbl">
                    <thead>
                      <tr>
                        <th>folder</th>
                        <th className="r">files</th>
                        <th className="r">size MB</th>
                        <th className="r">share</th>
                        <th className="r">largest MB</th>
                        <th className="r">average MB</th>
                        <th>last modified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...detail.files.folders]
                        .sort((a, b) => num(b.sizeMB) - num(a.sizeMB) || String(a.folderName).localeCompare(String(b.folderName)))
                        .map((f) => {
                          const share = pctOf(num(f.sizeMB), num(detail.files.totalSizeMB));
                          const empty = num(f.fileCount) === 0;
                          return (
                            <tr key={f.folderName} style={empty ? { opacity: 0.55 } : undefined}>
                              <td><b>{f.folderName}</b></td>
                              <td className="r">{f.fileCount}</td>
                              <td className="r">{mb(f.sizeMB)}</td>
                              <td className="r">
                                <div className="db-usage">
                                  <span className="db-usage__track"><i className="lv-lo" style={{ width: `${Math.min(100, share)}%` }} /></span>
                                  <span className="db-usage__pct">{fmtPct(share)}</span>
                                </div>
                              </td>
                              <td className="r">{mb(f.largestFileMB)}</td>
                              <td className="r">{mb(f.averageFileMB)}</td>
                              <td>
                                {f.lastModified ? (
                                  <>
                                    <div>{fmtDate(f.lastModified)}</div>
                                    <div className="db-sub">{ago(f.lastModified)}</div>
                                  </>
                                ) : <span className="db-zero">never</span>}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* tables */}
              <div className="db-h">// TABLES<span>{Array.isArray(detail.tables) ? detail.tables.length : 0} returned</span></div>
              {!Array.isArray(detail.tables) || detail.tables.length === 0 ? (
                <EmptyState>No table-level data returned for this client.</EmptyState>
              ) : (
                (() => {
                  const cols = [...new Set(detail.tables.flatMap((t) => Object.keys(t && typeof t === 'object' ? t : { value: t })))];
                  return (
                    <div className="db-tablewrap">
                      <table className="db-tbl">
                        <thead>
                          <tr>{cols.map((k) => <th key={k}>{humanKey(k)}</th>)}</tr>
                        </thead>
                        <tbody>
                          {detail.tables.map((t, i) => {
                            const o = t && typeof t === 'object' ? t : { value: t };
                            return (
                              <tr key={i}>
                                {cols.map((k) => (
                                  <td key={k} className={typeof o[k] === 'number' ? 'r' : ''}>
                                    {typeof o[k] === 'number' ? o[k].toLocaleString('en-US') : isIsoDate(o[k]) ? fmtDate(o[k]) : String(o[k] ?? '—')}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()
              )}

              {/* properties */}
              <div className="db-h">// DATABASE_PROPERTIES</div>
              <div className="db-kv">
                {Object.entries(detail.databaseProperties || {}).map(([k, v]) => (
                  <div key={k} className="db-kv__row">
                    <span className="db-kv__k">{humanKey(k)}</span>
                    <span>
                      {typeof v === 'boolean'
                        ? <span className={v ? 'db-ok' : 'db-dim'}>{String(v)}</span>
                        : isIsoDate(v)
                          ? <>{fmtDate(v)} <span className="db-sub">({ago(v)})</span></>
                          : String(v ?? '—')}
                    </span>
                  </div>
                ))}
                {Object.keys(detail.databaseProperties || {}).length === 0 && (
                  <div className="db-kv__row db-dim">no properties returned</div>
                )}
              </div>

              {/* raw */}
              <div className="db-h">// RAW_RESPONSE<span>exactly what the API returned</span></div>
              <details className="db-raw">
                <summary>show JSON</summary>
                <div style={{ padding: '8px 12px' }}>
                  <button className="term-btn term-btn--sm" onClick={copyRaw}>copy_json</button>
                </div>
                <pre className="db-raw__pre">{JSON.stringify(rawResp[activeDetail], null, 2)}</pre>
              </details>
            </>
          )}
        </Panel>
      )}
    </div>
  );
}